/* v3.18-1294 — Phone system access-control gate.
 * v3.19-1403 — CRM-SEC-001 Company Entitlement Lockdown.
 *
 * Two independent gates live here:
 *
 *   canUsePhoneSystem(user)       — legacy company/role gate (retained
 *     for backward compat with code paths that still key off it).
 *
 *   canUseIntegratedPBX(user)     — v3.19-1403 authoritative PBX
 *     entitlement.  ONLY users whose authenticated email domain is
 *     `prestigioustravelclub.org`, `easyquickfund.com`, or
 *     `creditforward360.com` may see PBX-only UI (dial pad, active-call
 *     widget, "Call Now" click-to-call button).  Role does NOT bypass
 *     — even an Admin from an external company is denied.
 *
 * External / unentitled companies fall back to the existing QR-code
 * "Call From Your Phone" workflow, which remains fully functional.
 *
 * NEVER trust this file as a security boundary.  The backend applies
 * `require_pbx_entitled_user` on every PBX endpoint independently.
 */

const PHONE_AUTHORIZED_COMPANY_KEYS = new Set([
  'ptc',
  'ptc inc',
  'ptc, inc',
  'ptc, inc.',
  'prestigious travel club',
  'prestigious travel club inc',
  'prestigious travel club, inc',
  'prestigious travel club, inc.',
  'prestigious travel club foundation',
  'ptc foundation',
  'easyquickfund',
  'easy quick fund',
  'easyquickfund.com',
  'eqf',
]);

const PTC_INTERNAL_ROLES = new Set([
  'ptc_admin',
  'ptc_director',
  'ptc_manager',
  'administrator',
]);

/** Return true if the current user may see / use the (legacy) phone system. */
export function canUsePhoneSystem(user) {
  if (!user) return false;
  const role = (user.role || '').toLowerCase();
  if (PTC_INTERNAL_ROLES.has(role)) return true;
  const company = (user.company_name || '').trim().toLowerCase();
  if (!company) return false;
  return PHONE_AUTHORIZED_COMPANY_KEYS.has(company);
}

/** Convenience label so gated UI can render a consistent message. */
export const PHONE_UNAUTHORIZED_MESSAGE =
  'Phone system access is limited to Prestigious Travel Club, Prestigious Travel Club Foundation, and EasyQuickFund.';

/* ============================================================
   v3.19-1403 CRM-SEC-001 — Integrated PBX Entitlement
   ============================================================ */
export const PBX_ENTITLED_DOMAINS = new Set([
  'prestigioustravelclub.org',
  'easyquickfund.com',
  'creditforward360.com',
]);

/** Extract the lowercased domain portion of a user's authenticated email. */
export function getUserEmailDomain(user) {
  const email = (user?.email || '').trim().toLowerCase();
  const at = email.indexOf('@');
  if (at < 0) return '';
  return email.slice(at + 1);
}

/** v3.19-1403 — authoritative frontend check.  Backend enforces the
 * same rule on every PBX endpoint regardless of what this returns. */
export function canUseIntegratedPBX(user) {
  if (!user) return false;
  return PBX_ENTITLED_DOMAINS.has(getUserEmailDomain(user));
}

export const PBX_UNENTITLED_MESSAGE =
  'Integrated phone system access is restricted to authorized internal companies. Scan the QR code to call from your phone.';
